<template>
  <div class="manage-proposal mb-5 mx-3 mt-3">
    <h1 class="mb-3">{{ isEdit ? 'Edit' : 'Add' }} Proposal</h1>

    <div class="card">
      <div class="card-body">
        <!-- Signatures Warning -->
        <div v-if="hasSignatures" class="alert alert-warning">
          <i class="fa fa-exclamation-triangle me-1"></i>
          Editing this proposal will clear <strong>all</strong> existing signatures.
        </div>
        <!-- HubSpot Deal -->
        <div class="row">
          <div class="col-md-9 col-12 mb-3">
            <label class="form-label">Available HubSpot Deals</label>
            <accordion-select :options="dealOptionsFormatted" :splitText="false" v-model="form.hubspot_deal_id" :class="{'is-invalid': errors.hubspot_deal_id}" 
              @update:modelValue="hubspotDealChanged" :disabled="loadingDeal" />
            <div v-if="errors.hubspot_deal_id" class="invalid-feedback">{{ errors.hubspot_deal_id }}</div>
          </div>
          <div class="col mb-3"  v-if="isEdit" >
            <label class="form-label">Status</label>
            <select class="form-select" v-model="form.status" :class="{'is-invalid': errors.status}" :disabled="loadingDeal" aria-label="Status">
              <option value="draft">Draft</option>
              <option value="sent">Sent</option>
              <option value="rejected">Rejected</option>
            </select>
            <div v-if="errors.status" class="invalid-feedback">{{ errors.status }}</div>
          </div>
        </div>
        <!-- Basic Info -->
        <div class="row">
          <div class="col-md-6 col-12 mb-3">
            <label class="form-label">Nonprofit Instance</label>
            <accordion-select :options="clientOptionsFormatted" :splitText="false" v-model="form.client_id" @update:modelValue="clientChanged" :disabled="loadingDeal" />
          </div>
          <div class="col-md-6 col-12 mb-3">
            <label class="form-label">Paid By (Client / Agency)</label>
            <accordion-select :options="paidByOptionsFormatted" :splitText="false" v-model="form.paid_by" @update:modelValue="paidByChanged" :class="{'is-invalid': errors.paid_by}" :disabled="loadingDeal" />
            <div v-if="errors.paid_by" class="invalid-feedback">{{ errors.paid_by }}</div>
          </div>
        </div>

        <div class="row mb-3">
          <div class="col-md-4 col-12 mb-3">
            <label class="form-label">Expiration Date</label>
            <DatepickerUtc v-model="form.expiration_date" :class="{'is-invalid': errors.expiration_date}" :disabled="loadingDeal" @update:modelValue="delete errors.expiration_date" />
            <div v-if="errors.expiration_date" class="invalid-feedback d-block">{{ errors.expiration_date }}</div>
          </div>
          
          
          <div class="col-md-3 col-12 mb-3">
            <label class="form-label">Proposal URL</label>
              <input type="text" v-model="form.url" @blur="validateUrl" class="form-control" :class="{'is-invalid': errors.url}" :disabled="loadingDeal" placeholder="proposal-url" aria-label="Proposal URL">
              <div v-if="errors.url" class="invalid-feedback">{{ errors.url }}</div>
              <small class="form-text text-muted">This will be used for the public proposal link.</small>
          </div>
          <div class="col-md-5 col-12 mb-3">
            <label class="form-label">Referral Agency (Only select if the agency is getting a referral payment)</label>
            <accordion-select :options="referralAgencyOptionsFormatted" :splitText="false" v-model="form.referral_agency_id" :disabled="loadingDeal" />
          </div>
        </div>

        <!-- Header / Footer -->
        <!-- Billing Contact -->
        <div v-if="paidByEntity" class="row">
          <h5 class="mb-2">Billing Information</h5>
          
          <div class="col-md-12 col-12 mb-3">
            <label class="form-label">Street Address</label>
            <input type="text" v-model="billingContact.address" @blur="saveBilling('address')" class="form-control" :class="{'is-invalid': errors.billing_address}" :disabled="loadingDeal" aria-label="Street Address">
            <div v-if="errors.billing_address" class="invalid-feedback">{{ errors.billing_address }}</div>
          </div>
          <div class="col-md-4 col-12 mb-3">
            <label class="form-label">City</label>
            <input type="text" v-model="billingContact.city" @blur="saveBilling('city')" class="form-control" :class="{'is-invalid': errors.billing_city}" :disabled="loadingDeal" aria-label="City">
            <div v-if="errors.billing_city" class="invalid-feedback">{{ errors.billing_city }}</div>
          </div>
          <div class="col-md-4 col-12 mb-3">
            <label class="form-label">State / Region</label>
            <input type="text" v-model="billingContact.region" @blur="saveBilling('region')" class="form-control" :class="{'is-invalid': errors.billing_region}" :disabled="loadingDeal" aria-label="State / Region">
            <div v-if="errors.billing_region" class="invalid-feedback">{{ errors.billing_region }}</div>
          </div>
          <div class="col-md-4 col-12 mb-3">
            <label class="form-label">Postal Code</label>
            <input type="text" v-model="billingContact.postal_code" @blur="saveBilling('postal_code')" class="form-control" :class="{'is-invalid': errors.billing_postal_code}" :disabled="loadingDeal" aria-label="Postal Code">
            <div v-if="errors.billing_postal_code" class="invalid-feedback">{{ errors.billing_postal_code }}</div>
          </div>
          <div class="col-md-8 col-12 mb-3">
            <label class="form-label">Country</label>
            <select class="form-select" v-model="billingContact.country" @change="saveBilling('country')" :class="{'is-invalid': errors.billing_country}" :disabled="loadingDeal" aria-label="Country">
              <option v-for="c in countries" :key="c.code" :value="c.code">{{ c.name }}</option>
            </select>
            <div v-if="errors.billing_country" class="invalid-feedback">{{ errors.billing_country }}</div>
          </div>
          <div class="col-md-4 col-12 mb-3" v-if="paidByEntity">
            <label class="form-label">Tax Exempt / Resale</label>
            <div class="form-check">
              <input class="form-check-input" type="checkbox" :checked="taxFlag" @change="toggleTaxFlag($event)"  aria-label="Tax Exempt / Resale">
              <label class="form-check-label">
                {{ paidByEntityType==='client' ? 'Tax Exempt' : 'Resale Certificate' }}
              </label>
            </div>
          </div>
        </div>

        <div class="row">
          <div class="col-12 mb-3">
            <label class="form-label">Proposal Custom Message</label>
            <WysiwygEditorAdvanced :input_value="form.custom_message" @wysiwygValueChange="val=>form.custom_message=val.value" :asset_id="'custom_message'" :disabled="loadingDeal" />
          </div>
        </div>

        <!-- Line Items Table - copy from subscription component -->
        <div class="card table-card mb-3">
          <div class="card-header d-flex justify-content-between align-items-center">
            <h4 class="mb-0">Line Items</h4>
            <button class="btn btn-secondary" @click.prevent="addLineItem" :disabled="loadingDeal"><i class="fa fa-plus me-1"></i>Add Item</button>
          </div>
          <div v-if="errors.line_items" class="alert alert-danger m-3">{{ errors.line_items }}</div>
          <div class="card-body p-0">
            <table class="table mb-0">
              <thead>
                <tr>
                  <th></th>
                  <th style="width:250px">Item</th>
                  <th style="width:190px">Discount</th>
                  <th>Frequency</th>
                  <th style="width:140px">Term</th>
                  <th>Start</th>
                  <th style="width:74px">Qty</th>
                  <th v-if="showReferralEligible" class="text-center">Eligible</th>
                  <th width="1">Total</th>
                  <th width="1">Net</th>
                  <th width="1">Tax</th>
                  <th></th>
                </tr>
              </thead>
              <draggable tag="tbody" v-model="form.line_items" handle=".drag-handle" item-key="order">
                <template #item="{element,index}">
                  <tr class="align-middle">
                    <td class="drag-handle align-middle"><i class="fas fa-grip-vertical"></i></td>
                    <td class="align-middle">
                      <accordion-select :options="catalogOptionsFormatted" :splitText="false" v-model="element.stripe_catalog_id" @update:modelValue="catalogChanged(index)" :class="{'is-invalid': errors[`catalog_${index}`]}" :disabled="loadingDeal" />
                      <div v-if="errors[`catalog_${index}`]" class="invalid-feedback d-block">{{ errors[`catalog_${index}`] }}</div>
                    </td>
                    <td class="align-middle">
                      <accordion-select :options="couponOptionsFormatted" :splitText="false" v-model="element.stripe_coupon_id" @update:modelValue="couponChanged(index)" :disabled="loadingDeal" />
                    </td>
                    <td class="align-middle" :colspan="element.billing_frequency === 'once' ? 2 : 1">
                      <select class="form-select" v-model="element.billing_frequency" @change="frequencyChanged(index)" :disabled="loadingDeal" aria-label="Billing Frequency">
                        <option value="once">Once</option>
                        <option value="monthly">Monthly</option>
                        <option value="quarterly">Quarterly</option>
                        <option value="yearly">Yearly</option>
                      </select>
                    </td>
                    <td v-if="element.billing_frequency !== 'once'" class="align-middle">
                      <input type="number" min="1" class="form-control d-inline-block" style="width:60px;" v-model.number="element.term" @input="recalc(index)" :class="{'is-invalid': errors[`term_${index}`]}" :disabled="loadingDeal" aria-label="Number">
                      <small class="text-muted ms-1">{{ termUnit(element.billing_frequency) }}</small>
                      <div v-if="errors[`term_${index}`]" class="invalid-feedback d-block">{{ errors[`term_${index}`] }}</div>
                    </td>
                    <td class="align-middle">
                      <DatepickerUtc v-model="element.start_date" @update:modelValue="recalc(index)" :placeholder="(element.start_date? null : 'Start at Payment')" :disabled="loadingDeal" />
                    </td>
                    <td class="align-middle"><input type="number" min="1" class="form-control" v-model.number="element.quantity" @input="recalc(index)" :class="{'is-invalid': errors[`qty_${index}`]}" :disabled="loadingDeal" aria-label="Number"></td>
                    <td v-if="showReferralEligible" class="align-middle text-center"><input type="checkbox" class="form-check-input" v-model="element.referral_eligible" :disabled="loadingDeal" aria-label="Referral Eligible"></td>
                    <td class="align-middle">{{ currency_with_zero(element.total_amount) }}</td>
                    <td class="align-middle">{{ currency_with_zero(element.net_amount) }}</td>
                    <td class="align-middle">
                      <span v-if="taxLoading" class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                      <span v-else>{{ currency_with_zero(element.tax_amount) }}</span>
                    </td>
                    <td class="align-middle text-nowrap">
                      <button class="btn btn-none btn-sm me-1" @click.prevent="openDescriptionModal(index)" :disabled="loadingDeal"><img class="icon" src="/img/icons/avid-icons/edit.svg" alt=""></button>
                      <button class="btn btn-none btn-sm" @click.prevent="removeLineItem(index)" :disabled="loadingDeal"><img class="icon red-icon" src="/img/icons/avid-icons/trash.svg" alt=""></button>
                    </td>
                  </tr>
                </template>
              </draggable>
            </table>
          </div>
        </div>

        <!-- Totals -->
        <div class="row mb-3">
          <div class="col-md-6 col-12">
            <h4>Total Contract Value: {{ currency_with_zero(totalAmount) }}</h4>
            <h6 class="text-muted">Net: {{ currency_with_zero(netAmount) }}</h6>
            <h6 class="text-primary">ARR: {{ currency_with_zero(arrAmount) }}</h6>
            <h6 class="text-muted">Tax: {{ currency_with_zero(taxAmountTotal) }} <small class="text-muted">({{ taxReasonText }})</small></h6>
            <div class="mt-2">
              <small class="text-muted">
                <strong>Contract Start:</strong> {{ calculatedStartDate }} | <strong>Term:</strong> {{ termDisplay }}
              </small>
            </div>
          </div>
        </div>

        <button class="btn btn-primary" @click.prevent="save" :disabled="saveDisabled || loadingDeal">
          <img class="icon me-1" src="/img/icons/avid-icons/save.svg" alt="">Save
        </button>
        <div v-if="notice" class="alert alert-danger mt-2">{{ notice }}</div>
        <button class="btn btn-secondary ms-2" @click.prevent="back" :disabled="loadingDeal">
          <img class="icon me-1" src="/img/icons/avid-icons/close.svg" alt="">Cancel
        </button>
      </div>
    </div>

    <!-- Description Modal -->
    <div class="modal fade" id="descriptionModal" tabindex="-1">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header"><h5 class="modal-title">Edit Description</h5><button type="button" class="btn-close" data-bs-dismiss="modal" data-bs-toggle="tooltip" title="Close"></button></div>
          <div class="modal-body"><textarea class="form-control" rows="4" v-model="modalDescription"></textarea></div>
          <div class="modal-footer"><button class="btn btn-secondary" data-bs-dismiss="modal">Cancel</button><button class="btn btn-primary" @click="saveDescription" data-bs-dismiss="modal">Save</button></div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import draggable from 'vuedraggable';
import vSelect from 'vue3-select';
import AccordionSelect from '../components/AccordionSelect.vue';
import DatepickerUtc from '../components/DatepickerUtc.vue';
import WysiwygEditorAdvanced from '../components/WysiwygEditorAdvanced.vue';
import debounce from 'lodash.debounce';
import { allCountries } from 'country-region-data';
import Swal from 'sweetalert2';
export default {
  name: 'ManageProposal',
  components: { draggable, vSelect, AccordionSelect, DatepickerUtc, WysiwygEditorAdvanced },
  props: {
    proposalData: { type: Object, default: null },
    initialClients: { type: Array, default: () => [] },
    initialAgencies: { type: Array, default: () => [] },
    taxabilityReason: { type: String, default: '' },
  },
  data() {
    return {
      isEdit: false,
      saving: false,
      notice: '',
      errors: {},
      loadingDeal: false,
      form: {
        client_id: null,
        client_url: null,
        url: null,
        paid_by: null,
        status: 'draft',
        start_date: null,
        term: null,
        hubspot_deal_id: null,
        referral_agency_id: null,
        custom_message: '',
        expiration_date: null,
        line_items: []
      },
      clientOptions: this.initialClients,
      paidByOptions: [],
      agencyOptions: this.initialAgencies,
      dealOptions: [],
      catalogOptions: [],
      couponOptions: [],
      clientUrl: null,
      billingContact:{id:null, address:'',city:'',region:'',postal_code:'',country:'US'},
      countries: allCountries.map(c=>({code:c[1],name:c[0]})),
      billingContactDirty:false,
      taxFlagDirty:false,
      taxLoading: false,
      localTaxabilityReason: '',
      modalDescription: '',
      editingIndex: -1,
    };
  },
  computed: {
    totalAmount() { return this.form.line_items.reduce((a,li)=>a+Number(li.total_amount||0),0); },
    netAmount() { return this.form.line_items.reduce((a,li)=>a+Number(li.net_amount||0),0); },
    taxAmountTotal(){ return this.form.line_items.reduce((a,li)=>a+Number(li.tax_amount||0),0); },
    arrAmount() {
      const now = new Date();
      const currentMonth = now.getMonth();
      const currentYear = now.getFullYear();
      
      return this.form.line_items.reduce((arr, li) => {
        // Skip one-time charges
        if (li.billing_frequency === 'once') {
          return arr;
        }
        
        // Calculate monthly revenue for this line item
        const unitAmount = Number(li.unit_amount || 0);
        const quantity = Number(li.quantity || 1);
        const couponId = li.stripe_coupon_id ? (typeof li.stripe_coupon_id === 'object' ? li.stripe_coupon_id.value : li.stripe_coupon_id) : null;
        const coupon = couponId ? this.getCoupon(couponId) : null;
        
        let monthlyRevenue = unitAmount * quantity;
        
        // Apply coupon discount
        if (coupon) {
          if (coupon.percent_off) {
            monthlyRevenue = monthlyRevenue * (1 - coupon.percent_off / 100);
          } else if (coupon.amount_off) {
            monthlyRevenue = monthlyRevenue - (coupon.amount_off / 100);
          }
        }
        
        // Convert to monthly amount based on billing frequency
        let monthlyAmount = monthlyRevenue;
        switch (li.billing_frequency) {
          case 'monthly':
            monthlyAmount = monthlyRevenue;
            break;
          case 'quarterly':
            monthlyAmount = monthlyRevenue / 3;
            break;
          case 'yearly':
            monthlyAmount = monthlyRevenue / 12;
            break;
        }
        
        // Handle start date - if in the future, assume start of next month
        let startDate = li.start_date ? new Date(li.start_date) : now;
        if (startDate > now) {
          // Future start date - assume start of next month
          startDate = new Date(currentYear, currentMonth + 1, 1);
        }
        
        // Calculate how many months of revenue to include in ARR
        const startMonth = startDate.getMonth();
        const startYear = startDate.getFullYear();
        const monthsFromStart = ((currentYear - startYear) * 12) + (currentMonth - startMonth);
        
        // Include up to 12 months of revenue
        const monthsToInclude = Math.min(li.term, Math.max(0, 12 - monthsFromStart));
        
        return arr + (monthlyAmount * monthsToInclude);
      }, 0);
    },
    taxReasonText(){
      if (this.taxLoading) return 'Loading...';
      const map={
        customer_exempt:'No tax applied – customer is exempt from tax.',
        not_collecting:'No tax collected – you are not registered in this jurisdiction or a non-taxable product code was used.',
        not_subject_to_tax:'No tax imposed on this transaction.',
        not_supported:'No tax applied – jurisdiction/product not supported by Stripe.',
        portion_product_exempt:'Only a portion of the price is taxable; the rest is exempt.',
        portion_reduced_rated:'Only a portion of the price is taxed at a reduced rate.',
        portion_standard_rated:'Only a portion of the price is taxed at the standard rate.',
        product_exempt:'Product or service is nontaxable or exempt.',
        product_exempt_holiday:'Product/service not taxed due to a tax holiday.',
        proportionally_rated:'Shipping tax rate is weighted average of line items.',
        reduced_rated:'Taxed at a reduced rate.',
        reverse_charge:'No tax applied – buyer is responsible for accounting for tax.',
        standard_rated:'Taxed at the standard rate.',
        taxable_basis_reduced:'A reduced amount of the price is subject to tax.',
        zero_rated:'Taxed at a special rate of 0% or exempt (input VAT may still be deductible).',
      };
      const reason = this.localTaxabilityReason || this.taxabilityReason;
      return reason ? (map[reason] || reason) : '';
    },
    clientOptionsFormatted() { return this.clientOptions.map(c=>({ value:c.id, text:c.name })); },
    paidByOptionsFormatted() {
      let filtered=[];
      
      // Force reactivity by accessing the reactive property
      const clientId = this.form.client_id;
      
      if(clientId){
        const id = typeof clientId==='object'?clientId.value:clientId;
        const sel=this.clientOptions.find(c=>c.id===id);
        if(sel){
           filtered.push({value:sel.id,text:sel.name,type:'App\\Models\\Client'});

          // Add all agencies
          this.agencyOptions.forEach(a=>{
            filtered.push({value:a.id,text:a.name,type:'App\\Models\\Agency'});
          });
        }
      } else {
        filtered = this.paidByOptions.map(o=>({value:o.id,text:o.name,type:o.type||'App\\Models\\Client'}));
      }
      return filtered;
    },
    dealOptionsFormatted() { return this.dealOptions.map(d=>({value:d.id,text:d.name})); },
    catalogOptionsFormatted() { return this.catalogOptions.map(c=>({value:c.price_id,text:this.currency_with_zero(c.unit_amount/100),category:c.name,price:c.unit_amount/100,frequency:c.recurring})); },
    couponOptionsFiltered() {
      return this.couponOptions.filter(c => {
        // Skip coupons with null/undefined IDs
        if (!c.id) return false;

        // Show non-agency coupons
        if (!c.id.startsWith('agency_')) return true;

        // Extract values from AccordionSelect objects (consistent with save method)
        const paidById = this.form.paid_by != null && typeof this.form.paid_by === 'object' ? this.form.paid_by?.value : null;
        const paidByType = this.form.paid_by != null && typeof this.form.paid_by === 'object' ? this.form.paid_by?.type : null;
        const referralAgencyId = this.form.referral_agency_id != null && typeof this.form.referral_agency_id === 'object' ? this.form.referral_agency_id?.value : null;

        // Show agency coupon if paid_by is an agency with matching ID
        const paidByMatches = paidById &&
                              paidByType === 'App\\Models\\Agency' &&
                              c.id === `agency_${paidById}`;

        // Show agency coupon if referral agency is selected with matching ID
        const referralAgencyMatches = referralAgencyId &&
                                     c.id === `agency_${referralAgencyId}`;

        return paidByMatches || referralAgencyMatches;
      });
    },
    couponOptionsFormatted() { return this.couponOptionsFiltered.map(c=>({value:c.id,text:c.name})); },
    showReferralEligible() { return this.form.referral_agency_id; },
    referralAgencyOptionsFormatted() {
      // If paid_by is an agency, only show that agency
      if (this.paidByEntityType === 'agency') {
        const paidById = this.form.paid_by && typeof this.form.paid_by === 'object' ? this.form.paid_by.value : this.form.paid_by?.id;
        const agency = this.agencyOptions.find(a => a.id === paidById);
        return agency ? [{ value: agency.id, text: agency.name }] : [];
      }
      
      // Otherwise show all agencies
      return this.agencyOptions.map(agency => ({
        value: agency.id,
        text: agency.name
      }));
    },
    saveDisabled() { return this.saving || Object.keys(this.errors).length>0; },
    paidByEntity(){
      const id = this.form.paid_by && typeof this.form.paid_by==='object' ? this.form.paid_by.value : this.form.paid_by?.id;
      const type = this.form.paid_by && typeof this.form.paid_by==='object' ? this.form.paid_by.type : this.form.paid_by?.type;
      if(!id||!type) return null;
      if(type==='App\\Models\\Client') return this.clientOptions.find(c=>c.id===id);
      if(type==='App\\Models\\Agency') return this.agencyOptions.find(a=>a.id===id);
      return null;
    },
    paidByEntityType(){
      if(!this.paidByEntity) return null;
      return (this.form.paid_by && typeof this.form.paid_by==='object' && this.form.paid_by.type==='App\\Models\\Agency') ? 'agency':'client';
    },
    taxFlag(){
      if(!this.paidByEntity) return false;
      return this.paidByEntityType==='client' ? !!this.paidByEntity.tax_exempt : !!this.paidByEntity.resale_certificate;
    },
    calculatedStartDate() {
      if (!this.form.start_date) return 'Start at Payment';
      return this.date(this.form.start_date);
    },
    termDisplay(){ return this.form.term ? `${this.form.term} months` : 'Not calculated'; },
    // NEW: detect if proposal has signatures
    hasSignatures() {
      return this.isEdit && this.proposalData && Array.isArray(this.proposalData.signatures) && this.proposalData.signatures.length > 0;
    },
  },
  watch: {
    'form.client_id': {
      handler(val) { 
        this.clientChanged(); 
      },
      deep: true
    },
    'form.paid_by': {
      handler(val) { 
        this.paidByChanged();
        // Clear referral agency if paid_by is now an agency
        if (this.paidByEntityType === 'agency') {
          this.form.referral_agency_id = null;
        }
      },
      deep: true
    },
    'form.line_items': {
      handler() {
        // Calculate overall start/end dates from line items
        this.calculateOverallDates();
      },
      deep: true
    }
  },
  created() {
    this.paidByOptions=[...this.clientOptions,...this.agencyOptions];
    if(this.proposalData){
      this.isEdit=true;
      this.form={...this.proposalData, paid_by:{value:this.proposalData.paid_by_id,text:this.proposalData.paid_by?.name,type:this.proposalData.paid_by_type}};
      this.form.expiration_date=this.proposalData.expiration_date;
      this.clientUrl=this.proposalData.client?.url;
      // Calculate dates from line items if not already set
      if(!this.form.start_date || !this.form.term) {
        this.calculateOverallDates();
      }
      if(this.proposalData.paid_by)
        this.billingContact=this.proposalData.paid_by.billing_contact || {id:null,first_name:'',last_name:'', address:'',city:'',region:'',postal_code:'',country:''};
      else
        this.billingContact={id:null, first_name:'', last_name:'', address:'',city:'',region:'',postal_code:'',country:'US'};
    }
    if(!this.isEdit){
      this.clientChanged();
      this.addLineItem();
    }
  },
  mounted(){ this.loadBillingDropdowns(); },
  methods: {
    queueTaxEstimate: debounce(function(){ this.fetchTaxEstimate(); }, 500),

    async fetchTaxEstimate(){
      if(!this.form.line_items.length) return;

      const paidById = this.form.paid_by && typeof this.form.paid_by==='object' ? this.form.paid_by.value : this.form.paid_by?.id;
      const paidByType = this.form.paid_by && typeof this.form.paid_by==='object' ? this.form.paid_by.type : this.form.paid_by?.type;

      if(!paidById || !paidByType) return; // need payer info

      // Skip tax estimation if postal code or country is blank
      if(!this.billingContact.postal_code || !this.billingContact.country) {
        return;
      }

      // Skip tax estimation if no line items with products
      const validLineItems = this.form.line_items.filter(li => !!li.stripe_catalog_id);
      if(!validLineItems.length) return;

      this.taxLoading = true;

      // Build line items with discounted unit amounts so Stripe Tax is calculated on the net price
      const payload = {
        paid_by_id: paidById,
        paid_by_type: paidByType,
        billing_contact: this.billingContact,
        tax_exempt: this.taxFlag,
        line_items: this.form.line_items
          .filter(li => !!li.stripe_product_id)
          .map(li => {
            const catalogId = typeof li.stripe_catalog_id === 'object' ? li.stripe_catalog_id?.value : li.stripe_catalog_id;
            const qty = Number(li.quantity || 1);

            // Start with the original unit amount from the catalog
            let discountedUnit = Number(li.unit_amount || 0);

            // Apply coupon-based discount if one is selected
            const couponId = li.stripe_coupon_id
              ? (typeof li.stripe_coupon_id === 'object' ? li.stripe_coupon_id.value : li.stripe_coupon_id)
              : null;
            if (couponId) {
              const coupon = this.getCoupon(couponId);
              if (coupon) {
                if (coupon.percent_off) {
                  discountedUnit = discountedUnit * (1 - coupon.percent_off / 100);
                } else if (coupon.amount_off) {
                  // Flat amount is per line item, not per unit
                  const amtOff = coupon.amount_off / 100;
                  discountedUnit = Math.max(0, discountedUnit - amtOff);
                }
              }
            }

            return {
              stripe_catalog_id: catalogId,
              stripe_product_id: li.stripe_product_id,
              unit_amount: discountedUnit,
              quantity: qty,
            };
          }),
      };

      try{
        const res = await window.axios.post('/api/billing/tax-estimate', payload);
        const updated = res.data.data.line_items;
        updated.forEach((u,idx)=>{
          this.form.line_items[idx].tax_rate = u.tax_rate;
          // Tax amount comes back per unit, so multiply by quantity and term
          const li = this.form.line_items[idx];
          const periods = li.term ? Math.max(1, li.term) : 1;
          this.form.line_items[idx].tax_amount = u.tax_amount * li.quantity * periods;
        });
        this.localTaxabilityReason = res.data.data.taxability_reason;
      }catch(e){ console.error('Tax estimate failed',e); }
      finally{
        this.taxLoading = false;
      }
    },
    async loadBillingDropdowns(){
      const base='/api/billing';

      const dealsParams = this.isEdit && this.proposalData?.id ? { deal_id: this.proposalData.hubspot_deal_id } : {};
      const [catalogRes,couponRes,dealRes]=await Promise.all([
        window.axios.get(`${base}/stripe/catalog`),
        window.axios.get(`${base}/stripe/coupons`),
        window.axios.get(`${base}/hubspot/deals`, { params: dealsParams })
      ]);
      // Filter out old Avid pricing and Pathway pricing products
      const excludedProductIds = ['prod_T9iXe5u1uXZFpL', 'prod_T9iX8J9lEMMIlR', 'prod_TJajLy9knfsYDZ'];
      this.catalogOptions=catalogRes.data.data
        .filter(cat => !excludedProductIds.includes(cat.product_id))
        .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      this.couponOptions=couponRes.data.data;
      this.dealOptions=dealRes.data.data;

      // After catalog is loaded, update unit_amount for any existing line items that have a matching catalog entry
      this.form.line_items.forEach((li, idx) => {
        const catId = typeof li.stripe_catalog_id === 'object' ? li.stripe_catalog_id?.value : li.stripe_catalog_id;
        if (!catId) return;
        const cat = this.getCatalog(catId);
        if (cat) {
          li.unit_amount = cat.unit_amount / 100;
          li.name = cat.name;
          li.stripe_product_id = cat.product_id;
          // recalc totals since unit amount changed
          this.recalc(idx);
        }
        
        // Ensure "once" frequency items have term set to 1
        if (li.billing_frequency === 'once') {
          li.term = 1;
        }
      });
    },
    async hubspotDealChanged(){
      this.validateField('hubspot_deal_id', this.form.hubspot_deal_id);

      const id = this.form.hubspot_deal_id && typeof this.form.hubspot_deal_id==='object' ? this.form.hubspot_deal_id.value : this.form.hubspot_deal_id;
      if(!id) return;

      // Close the dropdown first
      this.closeAccordionSelectDropdown();

      // Wait for the dropdown to close before making the API call
      await this.$nextTick();

      this.loadingDeal = true;
      
      // Show SweetAlert for loading state
      const loadingSwal = Swal.fire({
        title: 'Fetching Deal Information',
        text: 'Please wait while we retrieve the deal details from HubSpot...',
        allowOutsideClick: false,
        allowEscapeKey: false,
        showConfirmButton: false,
        customClass: {
          popup: 'swal-info'
        },
        didOpen: () => {
          Swal.showLoading();
        }
      });

      try {
        const res = await window.axios.get(`/api/admin/billing/proposals/from-deal/${id}`);
        const data = res.data.data;

        // Update client dropdown if new client returned
        if(res.data.client && res.data.client.id){
          const idx = this.clientOptions.findIndex(c=>c.id===res.data.client.id);
          if(idx===-1) this.clientOptions.push(res.data.client);
        }

        Object.assign(this.form, data);

        // Generate URL-safe default value if URL is empty and we have client data
        if (!this.form.url && data.client && data.client.name) {
          this.form.url = this.generateUrlSafeValue(data.client.name);
        }
        
        // Regenerate proposal URL when deal is changed
        if (data.client && data.client.name) {
          this.form.url = this.generateUrlSafeValue(data.client.name);
        }

        // ensure tax fetched for returned items
        this.queueTaxEstimate();

        // Ensure paid_by object structure
        if(data.paid_by_id){
          const pbType = data.paid_by_type;
          const pbName = pbType==='App\\Models\\Agency' ? (this.agencyOptions.find(a=>a.id===data.paid_by_id)?.name) : (this.clientOptions.find(c=>c.id===data.paid_by_id)?.name);
          this.form.paid_by = {value:data.paid_by_id,text:pbName,type:pbType};
          this.billingContact = {
            ...(data.paid_by.billing_contact || {}),
            address: data.paid_by.billing_contact?.address || '',
            city: data.paid_by.billing_contact?.city || '',
            region: data.paid_by.billing_contact?.region || '',
            postal_code: data.paid_by.billing_contact?.postal_code || '',
            country: data.paid_by.billing_contact?.country || ''
          };
        }

        // Close loading alert
        await Swal.close();
        
      } catch (error) {
        await Swal.close();
        this.notice='Failed to fetch proposal draft from deal.';
      } finally {
        this.loadingDeal = false;
      }
    },
    closeAccordionSelectDropdown() {
      // Find and close any open vue-select dropdowns
      // Vue-select adds a class 'vs--open' when open
      const openSelects = document.querySelectorAll('.vs--open');
      openSelects.forEach(select => {
        // Remove the open class to close the dropdown
        select.classList.remove('vs--open');
        
        // Find and hide the dropdown menu
        const dropdownMenu = select.querySelector('.vs__dropdown-menu');
        if (dropdownMenu) {
          dropdownMenu.style.display = 'none';
          dropdownMenu.classList.remove('vs__open');
        }
        
        // Update aria attributes
        select.setAttribute('aria-expanded', 'false');
        const control = select.querySelector('.vs__control');
        if (control) {
          control.setAttribute('aria-expanded', 'false');
        }
      });

      // Also try to close any Bootstrap dropdowns that might be open
      const openDropdowns = document.querySelectorAll('.dropdown-menu.show');
      openDropdowns.forEach(dropdown => {
        const dropdownToggle = dropdown.previousElementSibling || dropdown.parentElement?.querySelector('[data-bs-toggle="dropdown"]');
        if (dropdownToggle) {
          const dropdownInstance = bootstrap.Dropdown.getInstance(dropdownToggle);
          if (dropdownInstance) {
            dropdownInstance.hide();
          } else {
            // Fallback: manually remove show class
            dropdown.classList.remove('show');
            if (dropdownToggle) {
              dropdownToggle.setAttribute('aria-expanded', 'false');
            }
          }
        }
      });
    },
    clientChanged(){
      if(!this.form.client_id) {
        return;
      }
      const id= typeof this.form.client_id==='object'?this.form.client_id.value:this.form.client_id;
      const sel=this.clientOptions.find(c=>c.id===id);
      if(sel){
        this.clientUrl=sel.url;
        this.form.client_url=sel.url;
        
        // Generate URL-safe default value if URL is empty
        if (!this.form.url) {
          this.form.url = this.generateUrlSafeValue(sel.name);
        }
        
        // Only update paid_by if it's not already set to an agency
        const currentPaidByType = this.form.paid_by && typeof this.form.paid_by==='object' ? this.form.paid_by.type : null;
        if (currentPaidByType !== 'App\\Models\\Agency') {
          this.form.paid_by={value:sel.id,text:sel.name,type:'App\\Models\\Client'};
        }
        
        this.validateField('client', id);
        this.validateField('paid_by', id);
      }
    },
    paidByChanged(){
      const paidById = typeof this.form.paid_by==='object'?this.form.paid_by?.value:this.form.paid_by;
      if(!paidById) {
        // Clear validation error if no paid by is selected (don't set error on initial load)
        if(this.errors.paid_by && this.errors.paid_by === 'Paid by is required') {
          delete this.errors.paid_by;
        }
        this.billingContact = {id:null,address:'',city:'',region:'',postal_code:'',country:'US'};
        this.billingContactDirty = false;
        return;
      }
      this.validateField('paid_by', paidById);
      
      // Update billing contact from the selected entity
      if(this.paidByEntity && this.paidByEntity.billing_contact) {
        this.billingContact = {
          id: this.paidByEntity.billing_contact.id || null,
          address: this.paidByEntity.billing_contact.address || '',
          city: this.paidByEntity.billing_contact.city || '',
          region: this.paidByEntity.billing_contact.region || '',
          postal_code: this.paidByEntity.billing_contact.postal_code || '',
          country: this.paidByEntity.billing_contact.country || 'US'
        };
      } else {
        this.billingContact = {id:null,address:'',city:'',region:'',postal_code:'',country:'US'};
      }
      this.billingContactDirty = false;
      
      this.queueTaxEstimate();
    },
    addLineItem(){
      this.form.line_items.push({description:'',stripe_catalog_id:null,stripe_product_id:null,unit_amount:0,stripe_coupon_id:null,start_date:null,quantity:1,total_amount:0,net_amount:0,billing_frequency:'monthly',referral_eligible:false,order:this.form.line_items.length,
        name:'', term:12, tax_rate:0, tax_amount:0
      });
      this.queueTaxEstimate();
    },
    removeLineItem(i){ this.form.line_items.splice(i,1); this.queueTaxEstimate(); },
    getCatalog(id){ return this.catalogOptions.find(c=>c.price_id===id); },
    getCoupon(id){ return this.couponOptions.find(c=>c.id===id); },
    catalogChanged(i){ 
      const li=this.form.line_items[i]; 
      const id=li.stripe_catalog_id != null && typeof li.stripe_catalog_id==='object'?li.stripe_catalog_id.value:li.stripe_catalog_id; 
      const cat=this.getCatalog(id); 
      
      if(cat) {
        li.unit_amount=cat.unit_amount/100; 
        li.name=cat.name;
        li.description=cat.description;
        li.stripe_product_id = cat.product_id;
      } else {
        // Product was cleared - reset values
        li.unit_amount=0; 
        li.name='';
        li.description='';
        li.stripe_product_id = null;
        li.tax_amount = 0;
        li.tax_rate = 0;
      }
      
      // If frequency is "once", set term to 1
      if(li.billing_frequency === 'once') {
        li.term = 1;
      }
      
      this.recalc(i);
      // Clear validation error for this field
      delete this.errors[`catalog_${i}`];
      this.queueTaxEstimate();
    },
    couponChanged(i){ this.recalc(i); },
    frequencyChanged(i){
      const li = this.form.line_items[i];
      // If frequency is set to "once", automatically set term to 1
      if(li.billing_frequency === 'once') {
        li.term = 1;
      }
      this.recalc(i);
    },
    recalc(i){
      const li=this.form.line_items[i];
      let periods=1;
      if(li.term){ periods = Math.max(1, li.term); }
      li.total_amount=li.unit_amount*li.quantity*periods;
      let net=li.total_amount;
      const couponId= li.stripe_coupon_id ? (typeof li.stripe_coupon_id==='object'?li.stripe_coupon_id.value:li.stripe_coupon_id):null;
      const coupon=couponId?this.getCoupon(couponId):null;
      if(coupon){
        if(coupon.percent_off) net=net*(1-coupon.percent_off/100);
        else if(coupon.amount_off) {
          // For recurring items, apply discount per billing period
          // For one-time items, apply discount once
          const discountAmount = coupon.amount_off / 100;
          const discountMultiplier = li.billing_frequency !== 'once' ? periods : 1;
          net = net - (discountAmount * discountMultiplier);
        }
      }
      li.net_amount=Math.max(0,net);
      
      // Clear validation errors for fields that are being updated
      delete this.errors[`term_${i}`];
      delete this.errors[`qty_${i}`];

      this.queueTaxEstimate();
    },
    validate(){
      this.errors={};
      this.notice='';
      const paidById= this.form.paid_by && typeof this.form.paid_by==='object' ? this.form.paid_by?.value : undefined;
      
      // Required basic fields
      if(!paidById) this.errors.paid_by='Paid by is required';
      if(!this.form.hubspot_deal_id) this.errors.hubspot_deal_id='HubSpot Deal is required';
      if(!this.form.expiration_date) this.errors.expiration_date='Expiration date is required';
      if(!this.form.url) this.errors.url='Proposal URL is required';
      
      // Validate URL format
      if(this.form.url) {
        const urlPattern = /^[a-zA-Z0-9_-]+$/;
        if (!urlPattern.test(this.form.url)) {
          this.errors.url = 'URL can only contain letters, numbers, hyphens, and underscores';
        } else if (this.form.url.startsWith('-') || this.form.url.endsWith('-') || this.form.url.startsWith('_') || this.form.url.endsWith('_')) {
          this.errors.url = 'URL cannot start or end with a hyphen or underscore';
        }
      }
      
      // Check if expiration date is after earliest start date from line items
      if(this.form.expiration_date) {
        const earliestStartDate = this.getEarliestStartDate();
        // Only validate if we have at least one line item with a valid start date
        if(earliestStartDate) {
          if(new Date(this.form.expiration_date) >= new Date(earliestStartDate)) {
            this.errors.expiration_date='Expiration date must be before the earliest start date';
          }
        }
      }
      
      // Billing address validation
      if(paidById) {
        if(!this.billingContact.address) this.errors.billing_address='Street address is required';
        if(!this.billingContact.city) this.errors.billing_city='City is required';
        if(!this.billingContact.region) this.errors.billing_region='State/Region is required';
        if(!this.billingContact.postal_code) this.errors.billing_postal_code='Postal code is required';
        if(!this.billingContact.country) this.errors.billing_country='Country is required';
      }
      
      // Line items validation
      if(this.form.line_items.length===0) {
        this.errors.line_items='At least one line item is required';
      } else {
        this.form.line_items.forEach((li,i)=>{
          const catId= typeof li.stripe_catalog_id==='object'?li.stripe_catalog_id?.value:li.stripe_catalog_id;
          if(!catId) this.errors[`catalog_${i}`] = 'Item is required';
          // Only validate term if frequency is not "once"
          if(li.billing_frequency !== 'once') {
            if(!li.term) this.errors[`term_${i}`] = 'Term is required';
            if(li.term && li.term <=0) this.errors[`term_${i}`]='Term must be >0';
          }
          if(!li.quantity || li.quantity <= 0) this.errors[`qty_${i}`] = 'Quantity must be >0';
        });
      }
      
      return Object.keys(this.errors).length===0;
    },
    validateField(field, value) {
      // Clear existing error for this field
      delete this.errors[field];
      
      // Validate specific field
      if(field === 'paid_by' && !value) {
        this.errors.paid_by = 'Paid by is required';
      }
    },
    async save(){
      if(!this.validate()) { this.notice='Please fix the highlighted errors.'; return; }
      this.saving=true;
      const payload={...this.form};
      payload.client_id= this.form.client_id && typeof this.form.client_id==='object' ? this.form.client_id.value: this.form.client_id;
      payload.url = this.form.url;
      payload.paid_by_id= this.form.paid_by && typeof this.form.paid_by==='object' ? this.form.paid_by.value:this.form.paid_by.id;
      payload.paid_by_type= this.form.paid_by && typeof this.form.paid_by==='object' ? this.form.paid_by.type:this.form.paid_by.type;
      payload.referral_agency_id= this.form.referral_agency_id && typeof this.form.referral_agency_id==='object' ? this.form.referral_agency_id.value:this.form.referral_agency_id;
      payload.hubspot_deal_id = this.form.hubspot_deal_id && typeof this.form.hubspot_deal_id==='object' ? this.form.hubspot_deal_id.value:this.form.hubspot_deal_id;
      payload.line_items=this.form.line_items.map(li=>({...li, stripe_catalog_id: typeof li.stripe_catalog_id==='object'?li.stripe_catalog_id.value:li.stripe_catalog_id, stripe_coupon_id: li.stripe_coupon_id && typeof li.stripe_coupon_id==='object'?li.stripe_coupon_id.value:li.stripe_coupon_id,
        name: li.name
      }));
      payload.expiration_date=this.form.expiration_date;
      payload.term=this.form.term;
      try{
        // persist billing contact if dirty
        const paidById = payload.paid_by_id;
        const paidByType = payload.paid_by_type;
        const typeSlug = paidByType==='App\\Models\\Agency' ? 'agency':'client';

        if(this.billingContactDirty){
          const bcPayload = {...this.billingContact};
          if(this.billingContact.id){
            await window.axios.put(`/api/billing/contact/${typeSlug}/${paidById}`,bcPayload);
          }else{
            await window.axios.post(`/api/billing/contact/${typeSlug}/${paidById}`,bcPayload);
          }
        }

        if(this.taxFlagDirty){
          await window.axios.put(`/api/billing/tax-flag/${typeSlug}/${paidById}`,{value:this.taxFlag});
        }

        // save proposal
        if(this.isEdit){ await window.axios.put(`/api/billing/proposals/${this.proposalData.id}`,payload); }
        else{ await window.axios.post(`/api/billing/proposals`,payload); }
        window.location.href='/admin/proposals';
      } catch (error) {
        if (error.response && error.response.status === 422) {
          // Handle validation errors from server
          const validationErrors = error.response.data.errors || {};
          let errorMessages = [];

          // Collect all error messages
          Object.values(validationErrors).forEach(messages => {
            if (Array.isArray(messages)) {
              errorMessages = errorMessages.concat(messages);
            } else {
              errorMessages.push(messages);
            }
          });

          // Show SweetAlert with validation errors
          await Swal.fire({
            icon: 'warning',
            title: 'Validation Error',
            html: errorMessages.join('<br>'),
            confirmButtonText: 'OK',
            customClass: {
              popup: 'swal-error'
            },
          });
        } else {
          // Handle other errors
          await Swal.fire({
            icon: 'error',
            title: 'Error',
            text: 'An unexpected error occurred. Please try again.',
            confirmButtonText: 'OK',
            customClass: {
              popup: 'swal-error'
            },
          });
        }
      } finally {
        this.saving=false;
        this.billingContactDirty=false;
        this.taxFlagDirty=false;
      }
    },
    back(){ window.history.back(); },
    date(dateString) {
      if (!dateString) return 'Not calculated';
      const date = new Date(dateString);
      return date.toLocaleDateString();
    },
    calculateOverallDates() {
      // Check if any line items have null start_date
      const hasNullStartDates = this.form.line_items.some(li => !li.start_date);
      
      if (hasNullStartDates) {
        this.form.start_date = null;
      } else {
        const starts = this.form.line_items.map(li => li.start_date).filter(Boolean).map(d => new Date(d));
        if (starts.length) {
          const min = new Date(Math.min(...starts));
          this.form.start_date = min.toISOString().split('T')[0];
        }
      }

      // Convert all line item terms to months, then take the max
      const frequencyToMonths = freq => {
        switch ((freq || '').toLowerCase()) {
          case 'monthly': return 1;
          case 'quarterly': return 3;
          case 'yearly': return 12;
          case 'once': return 1; // treat "once" as 1 month for display
          default: return 1;
        }
      };

      const termsInMonths = this.form.line_items.map(li => {
        const term = parseInt(li.term, 10) || 0;
        const freq = li.billing_frequency || 'monthly';
        return term * frequencyToMonths(freq);
      });

      if (termsInMonths.length) {
        this.form.term = Math.max(...termsInMonths);
      }
    },
    termUnit(freq) {
      switch ((freq || '').toLowerCase()) {
        case 'monthly':
          return 'months';
        case 'quarterly':
          return 'quarters';
        case 'yearly':
          return 'years';
        case 'once':
          return 'occurrence';
        default:
          return '';
      }
    },

    saveBilling(field){
      this.billingContactDirty = true;
      if(['address','city','region','postal_code','country'].includes(field)){
        this.queueTaxEstimate();
      }
      // Clear validation error for this field
      delete this.errors[`billing_${field}`];
    },

    async toggleTaxFlag(evt){
      const val = evt.target.checked;
      const id  = this.form.paid_by && typeof this.form.paid_by==='object' ? this.form.paid_by.value : this.form.paid_by?.id;
      const type= this.paidByEntityType;
      if(!id||!type) return;
      this.taxFlagDirty = true;
      if(type==='client'){
        const idx=this.clientOptions.findIndex(c=>c.id===id);
        if(idx>-1) this.clientOptions[idx].tax_exempt=val;
      }else{
        const idx=this.agencyOptions.findIndex(a=>a.id===id);
        if(idx>-1) this.agencyOptions[idx].resale_certificate=val;
      }
      this.queueTaxEstimate();
    },
    openDescriptionModal(i) {
      this.editingIndex = i;
      this.modalDescription = this.form.line_items[i].description || '';
      new bootstrap.Modal(document.getElementById('descriptionModal')).show();
    },
    saveDescription() {
      if (this.editingIndex >= 0) {
        this.form.line_items[this.editingIndex].description = this.modalDescription;
        this.editingIndex = -1;
      }
    },
    getEarliestStartDate() {
      // If any line item has a null/undefined/empty start date, return null
      const hasNullStartDate = this.form.line_items.some(li => 
        li.start_date === null || li.start_date === undefined || li.start_date === ''
      );
      
      if (hasNullStartDate) {
        return null;
      }
      
      // Get all start dates from line items
      const startDates = this.form.line_items.map(li => li.start_date);
      
      if (startDates.length === 0) {
        return null;
      }
      
      // Return the earliest start date
      return startDates.reduce((earliest, current) => {
        return new Date(current) < new Date(earliest) ? current : earliest;
      });
    },
    generateUrlSafeValue(name) {
      if (!name) return '';
      return name
        .toLowerCase() // Convert to lowercase for auto-generation
        .replace(/[^a-z0-9\s_-]/g, '') // Remove special characters except spaces, hyphens, and underscores
        .replace(/\s+/g, '-') // Replace spaces with hyphens
        .replace(/-+/g, '-') // Replace multiple hyphens with single hyphen
        .replace(/^-|-$/g, '') // Remove leading/trailing hyphens
        .replace(/^_|_$/g, ''); // Remove leading/trailing underscores
    },
    validateUrl() {
      // Clear existing error
      delete this.errors.url;
      
      if (!this.form.url) {
        this.errors.url = 'Proposal URL is required';
        return;
      }
      
      // Check if URL contains only valid characters
      const urlPattern = /^[a-zA-Z0-9_-]+$/;
      if (!urlPattern.test(this.form.url)) {
        this.errors.url = 'URL can only contain letters, numbers, hyphens, and underscores';
        return;
      }
      
      // Check if URL starts or ends with hyphen or underscore
      if (this.form.url.startsWith('-') || this.form.url.endsWith('-') || this.form.url.startsWith('_') || this.form.url.endsWith('_')) {
        this.errors.url = 'URL cannot start or end with a hyphen or underscore';
        return;
      }
    },
  }
};
</script>
